/**
 * Business Hours Validation Utility
 * 
 * Parses and validates business hours strings and checks if current time
 * falls within business hours for a given location.
 */

export interface BusinessHours {
  days: string[]; // e.g., ["Mon", "Tue", "Wed"]
  startTime: string; // e.g., "9AM" or "09:00"
  endTime: string; // e.g., "5PM" or "17:00"
}

/**
 * "Monday through Friday 8 am - 5 pm" uses "through" (not a hyphen range) and
 * often omits ":" before times — expandDaySegment + the main regex would miss it.
 */
function preprocessThroughSeparatedDayRanges(input: string): string {
  return input
    .replace(/\b(Monday|Mon)\s+(?:through|thru|to|-)\s+(Friday|Fri)\b/gi, "Mon-Fri")
    .replace(/\b(Monday|Mon)\s+(?:through|thru|to|-)\s+(Thursday|Thu|Thur|Thurs)\b/gi, "Mon-Thu")
    .replace(/\b(Monday|Mon)\s+(?:through|thru|to|-)\s+(Wednesday|Wed)\b/gi, "Mon-Wed")
    .replace(/\b(Tuesday|Tue|Tues)\s+(?:through|thru|to|-)\s+(Friday|Fri)\b/gi, "Tue-Fri")
    .replace(/\b(Tuesday|Tue|Tues)\s+(?:through|thru|to|-)\s+(Thursday|Thu|Thurs)\b/gi, "Tue-Thu")
    .replace(/\b(Wednesday|Wed)\s+(?:through|thru|to|-)\s+(Friday|Fri)\b/gi, "Wed-Fri")
    .replace(/\b(Sunday|Sun)\s+(?:through|thru|to|-)\s+(Saturday|Sat)\b/gi, "Sun-Sat")
    .replace(/\b(Monday|Mon)\s+(?:through|thru|to|-)\s+(Sunday|Sun)\b/gi, "Mon-Sun")
    .replace(/\b(Monday|Mon)\s+(?:through|thru|to|-)\s+(Saturday|Sat)\b/gi, "Mon-Sat");
}

/** Day token used when extracting schedule blocks from free-form hours strings. */
const DAY_NAME =
  "Monday|Mon|Tues|Tue|Tuesday|Wed(?:nesday)?|Thurs|Thur|Thu(?:rsday)?|Fri(?:day)?|Sat(?:urday)?|Sun(?:day)?";

/** "Mon - Fri" → "Mon-Fri" so block extraction does not treat Fri as a separate block. */
function collapseSpacedDayRanges(input: string): string {
  const dayRangeRegex = new RegExp(`\\b(${DAY_NAME})\\s+-\\s+(${DAY_NAME})\\b`, "gi");
  return input.replace(dayRangeRegex, "$1-$2");
}

/**
 * Extract (daySegment, startTime, endTime) blocks from normalized hours text.
 * Handles en-dash/colon separators, comma/ampersand day lists, and back-to-back
 * blocks without commas (e.g. "... 4:00 pm Tue & Thur – 7:00 am - 6:00 pm").
 */
function extractBusinessHourBlocks(normalized: string): Array<[string, string, string]> {
  const blockRegex = new RegExp(
    `\\b((?:${DAY_NAME})(?:\\s*(?:[,\\&]|\\band\\b|-)\\s*(?:${DAY_NAME}))*)` +
      `\\s*(?:[-:]\\s*|\\s+(?=\\d))` +
      `(\\d{1,2}(?::\\d{2})?\\s*(?:[AaPp][Mm])?)\\s*-\\s*(\\d{1,2}(?::\\d{2})?\\s*(?:[AaPp][Mm])?)`,
    "gi",
  );

  const blocks: Array<[string, string, string]> = [];
  for (const match of normalized.matchAll(blockRegex)) {
    const daySegment = match[1]?.trim();
    const startTime = match[2]?.trim();
    const endTime = match[3]?.trim();
    if (!daySegment || !startTime || !endTime) continue;
    if (/closed/i.test(daySegment) || /closed/i.test(endTime)) continue;
    blocks.push([daySegment, startTime, endTime]);
  }
  return blocks;
}

/**
 * Parse business hours string into structured format
 * Supports formats like:
 * - "Mon-Fri: 9AM-5PM"
 * - "Mon-Fri: 9AM-5PM, Sat: 10AM-2PM"
 * - "Monday-Friday: 9:00 AM - 5:00 PM"
 * - "Monday through Friday 8 am - 5 pm" (Settings-style prose)
 */
export function parseBusinessHours(businessHoursString: string | null | undefined): BusinessHours[] {
  if (!businessHoursString) {
    return [];
  }

  const hours: BusinessHours[] = [];
  const dayMap: Record<string, number> = {
    'mon': 1, 'monday': 1,
    'tue': 2, 'tues': 2, 'tuesday': 2,
    'wed': 3, 'wednesday': 3,
    'thu': 4, 'thur': 4, 'thurs': 4, 'thursday': 4,
    'fri': 5, 'friday': 5,
    'sat': 6, 'saturday': 6,
    'sun': 0, 'sunday': 0,
  };

  const expandDaySegment = (segment: string): number[] => {
    const normalized = segment
      .toLowerCase()
      .replace(/\./g, "")
      .replace(/\s+/g, " ")
      .trim();
    if (!normalized) return [];

    const tokens = normalized
      .split(/,|\band\b|&/i)
      .map((t) => t.trim())
      .filter(Boolean);

    const result: number[] = [];
    for (const token of tokens) {
      // Day range, e.g. "mon-fri" or "monday-friday"
      const [startRaw, endRaw] = token.split("-").map((d) => d.trim().toLowerCase());
      const start = dayMap[startRaw];
      if (start === undefined) continue;

      if (!endRaw) {
        result.push(start);
        continue;
      }

      const end = dayMap[endRaw];
      if (end === undefined) {
        result.push(start);
        continue;
      }

      let current = start;
      do {
        result.push(current);
        current = (current + 1) % 7;
      } while (current !== (end + 1) % 7);
    }

    return [...new Set(result)];
  };

  // Normalize: strip "Hours: " prefix, replace en-dash/em-dash with hyphen (DB often has "10:00AM – 6:30PM")
  const cleaned = preprocessThroughSeparatedDayRanges(
    businessHoursString
      .replace(/^Hours:\s*/i, '')
      .replace(/[\u2013\u2014\u2212]/g, '-') // en-dash, em-dash, minus sign → hyphen
      .replace(/\bto\b/gi, '-'),
  ); // "9:00 AM to 5:00 PM" → "9:00 AM - 5:00 PM" (after Hours strip)

  // Split by comma OR by detecting day labels (to handle formats like
  // "Monday: ... Tuesday: ..." and "Mon: ... Tues: ...").
  // Replace day label boundaries with comma-prefixed versions for consistency.
  // IMPORTANT: Do not match the trailing day in a hyphen range (e.g. "Monday-Sunday: 9-5")
  // — that would split into "Monday-Sun" + "Sunday: ..." and only Sunday would parse.
  const dayLabelRegex =
    /(?<!-)\b(Monday|Mon|Tuesday|Tues|Tue|Wednesday|Wed|Thursday|Thurs|Thur|Thu|Friday|Fri|Saturday|Sat|Sunday|Sun)\s*:\s*/gi;
  const normalized = collapseSpacedDayRanges(cleaned).replace(
    dayLabelRegex,
    (_match, day, offset) => {
      // If this is not the first day (offset > 0), add comma before it.
      return offset > 0 ? `, ${day}: ` : `${day}: `;
    },
  );

  for (const [daySegment, startTime, endTime] of extractBusinessHourBlocks(normalized)) {
    const days = expandDaySegment(daySegment);
    if (days.length === 0) continue;

    // Normalize time format - for end times without AM/PM (e.g. "6:30"), assume PM (typical close times)
    const normalizedStartTime = normalizeTime(startTime, false);
    const normalizedEndTime = normalizeTime(endTime, true);

    hours.push({
      days: days.map(d => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d]),
      startTime: normalizedStartTime,
      endTime: normalizedEndTime,
    });
  }

  return hours;
}

/**
 * Normalize time string to 24-hour format (HH:MM)
 * Handles formats like "9AM", "9:00 AM", "09:00", "5PM", "17:00"
 * @param assumePMForEndTime - When true and time has no AM/PM, assume PM for hours 1-11 (typical close times like "6:30")
 */
function normalizeTime(timeStr: string, assumePMForEndTime = false): string {
  const trimmed = timeStr.trim();
  
  // If already in 24-hour format (HH:MM), return as-is
  if (/^\d{2}:\d{2}$/.test(trimmed)) {
    return trimmed;
  }

  // Parse 12-hour format
  const match = trimmed.match(/(\d{1,2})(?::(\d{2}))?\s*(AM|PM)/i);
  if (!match) {
    // Try to parse as 24-hour without AM/PM
    const hourMatch = trimmed.match(/^(\d{1,2})(?::(\d{2}))?$/);
    if (hourMatch) {
      let hour = parseInt(hourMatch[1]);
      const minute = hourMatch[2] || '00';
      // End times like "6:30" without AM/PM are typically PM (6:30 PM), not AM
      if (assumePMForEndTime && hour >= 1 && hour <= 11) {
        hour += 12;
      }
      return `${hour.toString().padStart(2, '0')}:${minute}`;
    }
    return '09:00'; // Default fallback
  }

  let hour = parseInt(match[1]);
  const minute = match[2] || '00';
  const period = match[3].toUpperCase();

  if (period === 'PM' && hour !== 12) {
    hour += 12;
  } else if (period === 'AM' && hour === 12) {
    hour = 0;
  }

  return `${hour.toString().padStart(2, '0')}:${minute}`;
}

/**
 * Check if current time is within business hours
 * @param businessHoursString - Business hours string from location
 * @param timezone - Optional timezone (defaults to system timezone)
 * @param testTime - Optional test time override (for testing only, format: "YYYY-MM-DD HH:MM" or Date object)
 * @returns true if current time is within business hours
 */
export function isWithinBusinessHours(
  businessHoursString: string | null | undefined,
  timezone?: string,
  testTime?: string | Date
): boolean {
  if (!businessHoursString) {
    return false; // No business hours = not available
  }

  const hours = parseBusinessHours(businessHoursString);
  if (hours.length === 0) {
    return false;
  }

  // Determine the time to use for validation
  // If testTime (clientTime) is provided, use it; otherwise use current time
  const timeForCheck = testTime 
    ? (typeof testTime === 'string' ? new Date(testTime) : testTime)
    : new Date();
  
  // Get day and time components in the specified timezone if available, otherwise local time of timeForCheck
  let currentDay: number;
  let currentTime: string;
  
  if (timezone) {
    try {
      const dayFormatter = new Intl.DateTimeFormat("en-US", {
        timeZone: timezone,
        weekday: "short",
      });
      const timeFormatter = new Intl.DateTimeFormat("en-US", {
        timeZone: timezone,
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });

      const dayName = dayFormatter.format(timeForCheck);
      const timeParts = timeFormatter.formatToParts(timeForCheck);
      const hour = timeParts.find((p) => p.type === "hour")?.value || "00";
      const minute = timeParts.find((p) => p.type === "minute")?.value || "00";

      const dayMap: Record<string, number> = {
        Sun: 0,
        Mon: 1,
        Tue: 2,
        Wed: 3,
        Thu: 4,
        Fri: 5,
        Sat: 6,
      };
      currentDay = dayMap[dayName] ?? timeForCheck.getDay();
      currentTime = `${hour.padStart(2, "0")}:${minute.padStart(2, "0")}`;
    } catch {
      currentDay = timeForCheck.getDay();
      currentTime = `${timeForCheck.getHours().toString().padStart(2, "0")}:${timeForCheck.getMinutes().toString().padStart(2, "0")}`;
    }
  } else {
    currentDay = timeForCheck.getDay(); // 0 = Sunday, 1 = Monday, etc.
    currentTime = `${timeForCheck.getHours().toString().padStart(2, "0")}:${timeForCheck.getMinutes().toString().padStart(2, "0")}`;
  }

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const currentDayName = dayNames[currentDay];

  // Find matching business hours for current day
  for (const hourBlock of hours) {
    if (hourBlock.days.includes(currentDayName)) {
      // Check if current time is within range
      if (currentTime >= hourBlock.startTime && currentTime <= hourBlock.endTime) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Get list of available days from business hours
 * @param businessHoursString - Business hours string from location
 * @returns Array of day names that are open (e.g., ["Mon", "Tue", "Wed", "Thu", "Fri"])
 */
export function getAvailableDays(businessHoursString: string | null | undefined): string[] {
  if (!businessHoursString) {
    return [];
  }
  
  const hours = parseBusinessHours(businessHoursString);
  const allDays = new Set<string>();
  
  for (const hourBlock of hours) {
    for (const day of hourBlock.days) {
      allDays.add(day);
    }
  }
  
  // Return in day-of-week order
  const dayOrder = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return dayOrder.filter(day => allDays.has(day));
}

/**
 * Get formatted business hours string for display
 * @param businessHoursString - Business hours string from location
 * @returns Formatted string for display
 */
export function formatBusinessHours(businessHoursString: string | null | undefined): string {
  if (!businessHoursString) {
    return 'Not specified';
  }

  return normalizeBusinessHoursString(businessHoursString) ?? 'Not specified';
}

/**
 * Normalize raw business-hours strings for prompt/display use.
 * - Strips "Hours:" prefix
 * - Normalizes dash variants to "-"
 * - Collapses repeated whitespace
 * - Adds separators for space-separated day blocks (e.g. "Mon: ... Tues: ...")
 */
export function normalizeBusinessHoursString(
  businessHoursString: string | null | undefined
): string | null {
  if (!businessHoursString?.trim()) {
    return null;
  }

  const dayLabelRegex =
    /(?<!-)\b(Monday|Mon|Tuesday|Tues|Tue|Wednesday|Wed|Thursday|Thurs|Thur|Thu|Friday|Fri|Saturday|Sat|Sunday|Sun)\s*:\s*/gi;

  // Collapse "Monday - Friday" → "Monday-Friday" BEFORE day-label splitting, otherwise
  // "Friday:" inside a range is treated as a new day block and Thu–Wed hours are lost.
  const normalized = collapseSpacedDayRanges(
    preprocessThroughSeparatedDayRanges(
      businessHoursString
        .replace(/^Hours:\s*/i, "")
        .replace(/[\u2013\u2014\u2212]/g, "-")
        .replace(/\bto\b/gi, "-"),
    ),
  )
    .replace(/\s+/g, " ")
    .trim()
    .replace(dayLabelRegex, (match, day, offset) =>
      offset > 0 ? `; ${day}: ` : `${day}: `,
    );

  return normalized;
}

/**
 * Check if a specific date falls on an open day
 * @param businessHoursString - Business hours string from location
 * @param date - Date to check (Date object or YYYY-MM-DD string)
 * @returns true if the date falls on an open day, false otherwise
 */
export function isOpenOnDate(
  businessHoursString: string | null | undefined,
  date: Date | string
): boolean {
  if (!businessHoursString) return false;
  const availableDays = getAvailableDays(businessHoursString);
  if (availableDays.length === 0) return false;

  const dateObj = typeof date === 'string'
    ? new Date(date.includes('T') ? date : date + 'T12:00:00')
    : date;

  const dayOfWeek = dateObj.getDay();
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const dayName = dayNames[dayOfWeek];
  return availableDays.includes(dayName ?? '');
}

// ─────────────────────────────────────────────────────────────────────────────
// AgentPhone inbound greeting helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Format a "HH:MM" 24-hour string to a natural "10 AM" / "2:30 PM" string.
 */
function formatOpenTime(time: string): string {
  const [h, m] = time.split(':').map(Number);
  const period = (h ?? 0) >= 12 ? 'PM' : 'AM';
  const hour12 = (h ?? 0) > 12 ? (h ?? 0) - 12 : (h ?? 0) === 0 ? 12 : (h ?? 0);
  const minStr = (m ?? 0) > 0 ? `:${(m ?? 0).toString().padStart(2, '0')}` : '';
  return `${hour12}${minStr} ${period}`;
}

/**
 * Returns a human-readable description of when the office next opens.
 * e.g. "Monday, March 23 at 10 AM"  or  "tomorrow at 9 AM"
 */
export function getNextOpenTime(
  businessHoursString: string | null | undefined,
  timezone?: string,
  testTime?: Date,
): string {
  const hours = parseBusinessHours(businessHoursString);
  if (hours.length === 0) return 'soon';

  const now = testTime ?? new Date();
  const shortDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const fullDays  = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const months    = ['January','February','March','April','May','June','July','August','September','October','November','December'];

  for (let i = 0; i <= 7; i++) {
    const checkDate = new Date(now.getTime() + i * 86_400_000);

    let dayIdx: number;
    if (timezone) {
      const fmt = new Intl.DateTimeFormat('en-US', { timeZone: timezone, weekday: 'short' });
      const formatted = fmt.format(checkDate);
      dayIdx = shortDays.indexOf(formatted);
    } else {
      dayIdx = checkDate.getDay();
    }

    const shortDay = shortDays[dayIdx] ?? '';
    for (const block of hours) {
      if (!block.days.includes(shortDay)) continue;

      const [openH, openM] = block.startTime.split(':').map(Number);
      let candidate: Date;

      if (timezone) {
        // Build "openH:openM on the same calendar date as checkDate in this timezone"
        // as a proper UTC timestamp.
        const tzDateStr = new Intl.DateTimeFormat('en-CA', {
          timeZone: timezone,
          year: 'numeric', month: '2-digit', day: '2-digit',
        }).format(checkDate); // "YYYY-MM-DD"

        const hh = String(openH ?? 0).padStart(2, '0');
        const mm = String(openM ?? 0).padStart(2, '0');

        // First pass: treat as UTC to get a Date close to the right moment
        const approx = new Date(`${tzDateStr}T${hh}:${mm}:00Z`);

        // Find what hour/minute the target timezone shows for this approx Date
        const displayParts = new Intl.DateTimeFormat('en-US', {
          timeZone: timezone, hour: '2-digit', minute: '2-digit', hour12: false,
        }).formatToParts(approx);
        const displayH = parseInt(displayParts.find(p => p.type === 'hour')?.value ?? '0');
        const displayM = parseInt(displayParts.find(p => p.type === 'minute')?.value ?? '0');

        // Shift approx so local time in `timezone` matches openH:openM (handles DST for most cases)
        const diffMs = ((( openH ?? 0) - displayH) * 60 + ((openM ?? 0) - displayM)) * 60_000;
        candidate = new Date(approx.getTime() + diffMs);
      } else {
        candidate = new Date(checkDate);
        candidate.setHours(openH ?? 0, openM ?? 0, 0, 0);
      }

      if (candidate > now) {
        const timeStr = formatOpenTime(block.startTime);
        if (i === 0) return `at ${timeStr} today`;
        if (i === 1) return `tomorrow at ${timeStr}`;
        const month = months[checkDate.getMonth()] ?? '';
        return `${fullDays[dayIdx] ?? shortDay}, ${month} ${checkDate.getDate()} at ${timeStr}`;
      }
    }
  }
  return 'soon';
}

/**
 * Build the AgentPhone inbound_message greeting based on current business status.
 * Returns different text when the office is open vs closed.
 */
export function buildInboundGreeting(opts: {
  orgName: string;
  agentName: string;
  businessHoursString: string | null | undefined;
  timezone?: string;
}): string {
  const { orgName, agentName, businessHoursString, timezone } = opts;
  const isOpen = isWithinBusinessHours(businessHoursString, timezone);

  if (isOpen) {
    return (
      `Thanks for calling ${orgName}. ` +
      `I'm ${agentName}, your virtual assistant. ` +
      `Our team is currently with other patients, but I can help with appointments or deliver your message. ` +
      `May I ask who's calling?`
    );
  }

  const nextOpen = getNextOpenTime(businessHoursString, timezone);
  const reopenPhrase = nextOpen && nextOpen !== 'soon' ? ` and will reopen ${nextOpen}` : '';
  return (
    `Thanks for calling ${orgName}. ` +
    `I'm ${agentName}, your virtual assistant. ` +
    `Our office is currently closed${reopenPhrase}. ` +
    `I can take your information and make sure the team follows up with you. ` +
    `May I ask who's calling?`
  );
}